/**
 * Admin — Revenue Calculation, the titration ladder (card 1244).
 *
 * A CONTROL, not a legend. One row per dosage step of a product, in catalog
 * `display_order`; pressing a step re-renders every figure in the timeline beneath it.
 * Split out of RevenueTimeline.jsx so each file keeps one job and stays under 300 lines.
 *
 * 🔑 THE SELECTED STEP DRIVES THE PANEL — the timeline never falls back to `dosages[0]`.
 * See docs/oval/project-oval-revenue/findings/DOSAGE-SOURCE-OF-TRUTH.md: dosage is a
 * point-of-sale fact, and a timeline that quietly showed the cheapest step would
 * under-report cost for every titrated customer.
 */

import { formatMetricValue, isBlockedMetric, describeMetricBasis } from '../../utils/revenueMetrics';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/** Pure. One figure with its basis tooltip; blocked values keep the blocked styling. */
const Figure = ({ metric }) => (
  <span
    className={isBlockedMetric(metric) ? styles.blockedValue : styles.metricValue}
    data-blocked={String(isBlockedMetric(metric))}
    title={describeMetricBasis(metric)}
  >
    {formatMetricValue(metric)}
  </span>
);

/**
 * Pure. Where the medication figure changes as the ladder is climbed — stated in words.
 *
 * 🔑 AN ABSENCE OF MARKERS IS A MEASURED FACT, NOT A DEAD FEATURE. `products.medication_cost`
 * is a PRODUCT-level column, so on most products every step costs the same and no marker
 * fires. Measured on live: 8 of the 95 product × term groups with more than one step DO
 * change (a DoctorNow `ourCost` step blocking while its neighbour resolves). Saying so
 * out loud is the difference between "nothing changed" and "this panel is broken".
 */
export const LadderCaption = ({ ladder }) => {
  const changes = ladder.filter((step) => step.costChanged);
  return (
    <p className={styles.ladderCaption} data-testid="ladder-caption" data-changes={changes.length}>
      {changes.length === 0
        ? 'Medication cost does not change across this ladder — products.medication_cost is a '
          + 'product-level column, so every step of this product is priced identically.'
        : `Medication cost changes at ${changes.length} `
          + `${changes.length === 1 ? 'step' : 'steps'}: `
          + `${changes.map((step) => step.dosageAmount ?? 'unnamed step').join(', ')}.`}
    </p>
  );
};

/** Pure. The titration ladder — a control. Selecting a step re-renders everything below. */
const TitrationLadder = ({ ladder, selectedDosageId, onSelect }) => (
  <ol className={styles.ladder} data-testid="titration-ladder" data-steps={ladder.length}>
    {ladder.map((step, index) => (
      <li key={step.dosageId ?? index}>
        <button
          type="button"
          className={styles.ladderStep}
          data-testid="ladder-step"
          data-dosage-id={step.dosageId ?? ''}
          data-selected={String(step.dosageId === selectedDosageId)}
          data-cost-changed={String(step.costChanged)}
          aria-pressed={step.dosageId === selectedDosageId}
          onClick={() => onSelect(step.dosageId)}
        >
          <span className={styles.ladderDose}>{step.dosageAmount ?? 'No dosage recorded'}</span>
          <span className={styles.ladderLabel}>
            {step.row?.dosage?.stepLabel ?? 'Step not described'}
            {step.row?.dosage?.isDefaultStep && <em className={styles.defaultTag}>catalog default</em>}
          </span>
          <span className={styles.ladderSku}>
            {step.row?.dosage?.sku
              ? <>Vial / SKU <code>{step.row.dosage.sku}</code></>
              : <span className={styles.notSold}>No vial/SKU recorded</span>}
          </span>
          <span className={styles.ladderCost}>
            <Figure metric={step.row?.medicationCost} />
            {step.costChanged && <em className={styles.costChange}>cost changes here</em>}
          </span>
        </button>
      </li>
    ))}
  </ol>
);

export default TitrationLadder;
